import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { getColumnDefs, exportColumnDefs, icon_fontSize } from './timesheet-config';
import { paginationTimesheetParams } from '@shared/config/table.config';
import { SearchService } from '@services/search.service';
import {
  TimesheetResponse,
  TimeSheetData,
  TimesheetUpdateResponse,
  TimesheetColumnDef,
  LeaveCommentsResponse,
} from '@interfaces/vendor-management-model';
import { ErrorMessages } from '@constants/constants';
import {
  Priority,
  TimesheetStatus,
  PersonaType,
  TimesheetEnum,
} from '@constants/global.enum';
import { TimesheetService } from './timesheet.service';
import { AuthService } from '@core/auth.service';
import { ToasterService } from '@services/toaster.service';
import { DataService } from '@services/data.service';
import { TIMESHEET_CONSTANTS } from '@pages/vendor-rep/timesheet/timesheet.constants';
import { sortByStatusPriority } from '@shared/helpers/timesheet-sort.helper';
import {
  getDataLoadedStatus,
  shouldShowTimesheetCheckbox,
  validateTimesheetRow,
  filterActionableRows,
  getHoursSuffix,
  ComponentType
} from '@shared/helpers/timesheet.helper';
import { ExportService } from '@services/export.service';
import { TimesheetCalendarService } from '@pages/vendor-rep/timesheet-calendar/timesheet-calendar.service';
import { catchError, forkJoin, map, of } from 'rxjs';
import { dropdownValues } from '@interfaces/common.model';
import { TeamManagementService } from '@pages/team-management/team-management.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-timesheet',
  templateUrl: './timesheet.component.html',
  styleUrl: './timesheet.component.scss',
})
export class TimesheetComponent implements OnInit {
  columnDefs: TimesheetColumnDef[] = getColumnDefs();
  monthOptions: { label: string; value: string; selected?: boolean }[] = [];
  paginationParams = paginationTimesheetParams;
  rowData: TimeSheetData[] = [];
  displayData: TimeSheetData[] = [];
  filteredData: TimeSheetData[] = [];
  filterValue: string = '';
  isDataLoaded: boolean = true;
  isLoading: boolean = false;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;
  priority = Priority;
  opened: boolean = false;
  iconFontSize = icon_fontSize;
  TimesheetStatus = TimesheetStatus;
  employeeId: string | null = null;
  selectedRows: TimeSheetData[] = [];
  componentName: string = PersonaType.Timesheet;
  timesheetHeading = TimesheetEnum;
  isFilteredData: boolean = false;
  disabledRowIds: { id: string[] } = { id: [] };
  isRowSelectableFunction = (row: TimeSheetData) => {
    return this.shouldShowCheckbox(row);
  };
  selectedRowData: {
    employeeId: string;
    employeeName: string;
    contractorName: string;
    jobTitle: string;
    location: string | null;
    vendorId: number | null;
    lastWorkingDay?: string;
    lastHireDate?: string;
    billingDates?: string[];
  } = {
    employeeId: '',
    employeeName: '',
    contractorName: '',
    jobTitle: '',
    location: null,
    vendorId: null
  };
  isTimesheetCalendarOpen = false;

  // Timesheets default to the previous month (they are reviewed a month in arrears).
  selectedyear: number = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getFullYear();
  selectedmonth: number = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getMonth() + 1;
  month: number = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getMonth() + 1;
  year: number = new Date().getFullYear();
  showContractorProfile: boolean = false;
  selectedContractorId: string = '';
  selectedOption: string = '';
  hoverTip = { show: false, text: '', x: 0, y: 0 };

  /* header-click sorting (vendor-flow look) — replaces the emerald arrow-icon sort */
  @ViewChild('customSortHeader', { static: true })
  customSortHeader!: TemplateRef<unknown>;
  sortField: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  private decorateColumnDefs(defs: TimesheetColumnDef[]): TimesheetColumnDef[] {
    return defs.map((def) =>
      def.sortParams?.enabled
        ? { ...def, sortParams: { enabled: false }, headerTemplate: this.customSortHeader }
        : def,
    );
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    const numericFields = ['workingHrs', 'billableHrs', 'leavesAvailed'];
    const dir = this.sortDir === 'asc' ? 1 : -1;
    this.displayData = [...this.displayData].sort((a: any, b: any) => {
      const av = a?.[field];
      const bv = b?.[field];
      if (numericFields.includes(field)) {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      }
      return (
        String(av ?? '')
          .toLowerCase()
          .localeCompare(String(bv ?? '').toLowerCase()) * dir
      );
    });
  }

  showTip(event: MouseEvent, text?: string | null): void {
    if (!text) return;
    const el = event.currentTarget as HTMLElement;
    if (el.scrollWidth <= el.clientWidth + 1) {
      return; // only show when the text is actually truncated
    }
    const rect = el.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 4 };
  }

  hideTip(): void {
    this.hoverTip = { ...this.hoverTip, show: false };
  }
  reporteeData: dropdownValues[] = [];
  selectedEmployee: string = 'all';
  activeEmployeeId: string = '';
  activeFilterType: string | undefined = undefined;
  loggedInEmployeeCode: string = '';
  activeFilterParams: {
    vendor?: string;
    status?: string;
    location?: string;
    dntLeader?: string;
    employeeStatus?: string;
  } = {};
  filterResetTrigger: number = 0;
  showReviewConfirmation: boolean = false;
  pendingReviewAction: string = '';
  private readonly incomingNavState: { month?: number; year?: number };

  constructor(
    private searchService: SearchService,
    private authService: AuthService,
    private timesheetService: TimesheetService,
    private timesheetCalendarService: TimesheetCalendarService,
    private teamReporteesService: TeamManagementService,
    private toasterService: ToasterService,
    private dataService: DataService,
    private exportService: ExportService,
    private readonly router: Router,
  ) {
    this.incomingNavState = this.router.getCurrentNavigation()?.extras?.state ?? {};
  }

  ngOnInit() {
    this.columnDefs = this.decorateColumnDefs(getColumnDefs());
    this.generateMonthOptions();
    this.applyIncomingMonthYear();
    this.month = this.selectedmonth;
    this.year = this.selectedyear;
    this.authService.getEmployeeDetails().then((employeeData) => {
      this.employeeId = employeeData?.employeeId || null;
      if (this.employeeId) {
        this.activeEmployeeId = this.employeeId;
        this.loadTimesheetData(
          this.employeeId,
          this.selectedmonth,
          this.selectedyear,
        );
        this.loadDirectReportees(this.employeeId);
      }
    });
  }

  generateMonthOptions(): void {
    const today = new Date();
    this.monthOptions = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return {
        label: `${monthName}, ${year}`,
        value: `${date.getMonth() + 1}-${year}`,
        selected: i === 1,
      };
    });

    if (!this.selectedOption && this.monthOptions.length > 1) {
      this.selectedOption = this.monthOptions[1].value;
    }
  }

  private applyIncomingMonthYear(): void {
    const monthParam = Number(this.incomingNavState.month);
    const yearParam = Number(this.incomingNavState.year);
    if (!monthParam || !yearParam) return;
    const targetValue = `${monthParam}-${yearParam}`;
    if (!this.monthOptions.some((o) => o.value === targetValue)) return;
    this.monthOptions = this.monthOptions.map((o) => ({
      ...o,
      selected: o.value === targetValue,
    }));
    this.selectedOption = targetValue;
    this.selectedmonth = monthParam;
    this.selectedyear = yearParam;
  }

  private loadTimesheetData(
    employeeId: string,
    month: number,
    year: number,
    vendor?: string,
    status?: string,
    location?: string,
    clientSideStatus?: string,
    dntLeader?: string,
    employeeStatus?: string,
  ) {
   this.timesheetService
      .getContractorsTimesheet(employeeId, month, year, {
        vendor,
        status,
        location,
        dntLeader,
        employeeStatus,
        filterType: this.activeFilterType,
      })
      .subscribe({
        next: (response: TimesheetResponse) => {
          let responseData = response?.data || [];

          if (clientSideStatus === TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION) {
            responseData = responseData.filter(
              (item) =>
                item.status === TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION ||
                (item.status === null &&
                  item.workingHrs === 0 &&
                  item.billableHrs === 0),
            );
          } else if (clientSideStatus === TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL) {
            responseData = responseData.filter(
              (item) => item.status === TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL,
            );
          }

          if (Array.isArray(responseData)) {
            this.rowData = responseData;
          } else {
            this.rowData = [];
          }
          this.disabledRowIds = { id: [] };
          this.rowData = this.rowData.map((row) => {
            const isPendingSubmission =
              row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION ||
              (row.status === null &&
                row.workingHrs === 0 &&
                row.billableHrs === 0);
            if (isPendingSubmission) {
              this.disabledRowIds.id.push(row.employeeId);
            }

            return {
              ...row,
              pendingSubmission: isPendingSubmission,
              selectable: !isPendingSubmission,
              selected: false,
            };
          });
          this.rowData = sortByStatusPriority(this.rowData, 'vendor-management');

          this.columnDefs = this.decorateColumnDefs(getColumnDefs(this.rowData));

          if (!this.isFilteredData) {
            this.displayData = this.rowData;
            this.filteredData = [];
            this.updateDataLoadedStatus(this.rowData);
          }
        },
        error: () => {
          this.isDataLoaded = false;
          this.toasterService.showError(
            TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_TIMESHEET,
            TIMESHEET_CONSTANTS.TITLES.ERROR
          );
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  navigateToContractorProfile(contractorId: string | number) {
    this.selectedContractorId = contractorId?.toString();
    this.showContractorProfile = !!this.selectedContractorId;
  }

  clearInput() {
    const sourceData = this.isFilteredData ? this.filteredData : this.rowData;
    this.displayData = [...sourceData];
    this.displayData = sortByStatusPriority(this.displayData, 'vendor-management');
    this.filterValue = '';
  }

    searchTimesheet(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(
      event as KeyboardEvent,
    );
    const sourceData = this.isFilteredData ? this.filteredData : this.rowData;
    
    this.displayData = sourceData?.length
      ? this.searchService.getSearchedData(
          sourceData,
          this.filterValue?.toLowerCase(),
        )
      : [];
    this.displayData = sortByStatusPriority(this.displayData, 'vendor-management');
  }

  onFilterApplied(data: TimeSheetData[]) {
    if (data?.length) {
      this.displayData = sortByStatusPriority(data, 'vendor-management');
      this.updateDataLoadedStatus(this.displayData);
    } else {
      this.isDataLoaded = false;
      this.errorMsg = ErrorMessages.NoDataAvailable;
    }
  }

  onTimesheetFilterApplied(filterPayload: any) {
    // MonthSelection inside the filter component fires a raw API refresh with buttonClick: ''.
    // If a client-side filter ('apply') is already active, ignore the raw overwrite.
    if (!filterPayload.buttonClick && this.isFilteredData) {
      return;
    }
    this.isLoading = true;
    
    if(filterPayload.buttonClick === 'reset'){
      this.isFilteredData = false;
      this.activeFilterParams = {};

      const eid = this.activeEmployeeId || this.employeeId!;
      if (eid) {
        this.loadTimesheetData(eid, this.selectedmonth, this.selectedyear);
      }
      this.opened = false;
      return;
    }
    
    this.isFilteredData = true;
    this.filteredData = filterPayload.data || [];
    this.displayData = [...this.filteredData];

    if (filterPayload.buttonClick === 'apply') {
      this.activeFilterParams = {
        vendor: filterPayload.vendor,
        status: filterPayload.status,
        location: filterPayload.location,
        dntLeader: filterPayload.dntLeader,
        employeeStatus: filterPayload.employeeStatus,
      };
    }

    if (this.filterValue) {
      this.displayData = this.filteredData.length
        ? this.searchService.getSearchedData(
            this.filteredData,
            this.filterValue?.toLowerCase(),
          )
        : [];
    }
    
    this.displayData = sortByStatusPriority(this.displayData, 'vendor-management');
    if (filterPayload.buttonClick === 'apply') {
      this.opened = false;
    }
    if (filterPayload.month && filterPayload.year) {
      this.selectedmonth = filterPayload.month;
      this.selectedyear = filterPayload.year;
    }  
    
    this.updateDataLoadedStatus(this.displayData);
  }

  onMonthSelect(event: any): void {
    this.selectedOption = event;
    const parsed = this.setMonthAndYear(this.selectedOption);
    this.selectedyear = Number(parsed.split('-')[1]);
    this.selectedmonth = Number(parsed.split('-')[0]);
    this.month = this.selectedmonth;
    this.year = this.selectedyear;
    const eid = this.activeEmployeeId || this.employeeId!;
    if (eid) {
      this.isFilteredData = false;
      this.filteredData = [];
      this.selectedRows = [];
      this.filterValue = '';
      this.loadTimesheetData(
        eid,
        this.selectedmonth,
        this.selectedyear,
        this.activeFilterParams.vendor,
        this.activeFilterParams.status,
        this.activeFilterParams.location,
        undefined,
        this.activeFilterParams.dntLeader,
        this.activeFilterParams.employeeStatus,
      );
    }
  }

  private setMonthAndYear(selectedOption: string): string {
    if (selectedOption.includes(', ')) {
      const [monthValue, yearValue] = selectedOption.split(', ');
      const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
      const formattedMonth = monthStr < 10 ? `0${monthStr}` : `${monthStr}`;
      return parseInt(formattedMonth) + '-' + yearValue;
    } else if (selectedOption.includes('-')) {
      
      return selectedOption;
    }
    const now = new Date();
    return (now.getMonth() + 1) + '-' + now.getFullYear();
  }

  private isLastDayOfMonth(date: Date): boolean {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    return date.getDate() === lastDay;
  }

  reviewTimesheet(action: string) {
    const today = new Date();
    const isCurrentMonth =
      this.getSelectedMonth() === today.getMonth() + 1 &&
      this.selectedyear === today.getFullYear();

    if (isCurrentMonth && !this.isLastDayOfMonth(today)) {
      this.toasterService.showError(
        TIMESHEET_CONSTANTS.MESSAGES.SUBMIT_ONLY_ON_LAST_DAY,
        TIMESHEET_CONSTANTS.TITLES.ERROR
      );
      return;
    }

    this.pendingReviewAction = action;
    this.showReviewConfirmation = true;
  }

  onConfirmReview(): void {
    this.showReviewConfirmation = false;
    const action = this.pendingReviewAction;
    if (action === TimesheetStatus.Rejected) {
      this.validateRejectCommentsAndReview(action);
      return;
    }
    this.updateTimesheetStatus(action);
  }

  onCancelReview(): void {
    this.showReviewConfirmation = false;
    this.pendingReviewAction = '';
  }

  get reviewConfirmationTitle(): string {
    return this.pendingReviewAction === TimesheetStatus.Approved
      ? TIMESHEET_CONSTANTS.APPROVE_CONFIRMATION_DIALOG.TITLE
      : this.pendingReviewAction === TimesheetStatus.NotApplicable
        ? TIMESHEET_CONSTANTS.CANCEL_CONFIRMATION_DIALOG.TITLE
        : TIMESHEET_CONSTANTS.REJECT_CONFIRMATION_DIALOG.TITLE;
  }

  get reviewConfirmationMessage(): string {
    return this.pendingReviewAction === TimesheetStatus.Approved
      ? TIMESHEET_CONSTANTS.APPROVE_CONFIRMATION_DIALOG.getMessage(this.selectedRows.length)
      : this.pendingReviewAction === TimesheetStatus.NotApplicable
        ? TIMESHEET_CONSTANTS.CANCEL_CONFIRMATION_DIALOG.getMessage(this.selectedRows.length)
        : TIMESHEET_CONSTANTS.REJECT_CONFIRMATION_DIALOG.getMessage(this.selectedRows.length);
  }

  get reviewConfirmationSubmitLabel(): string {
    return this.pendingReviewAction === TimesheetStatus.Approved
      ? TIMESHEET_CONSTANTS.APPROVE_CONFIRMATION_DIALOG.SUBMIT_LABEL
      : this.pendingReviewAction === TimesheetStatus.NotApplicable
        ? TIMESHEET_CONSTANTS.CANCEL_CONFIRMATION_DIALOG.SUBMIT_LABEL
        : TIMESHEET_CONSTANTS.REJECT_CONFIRMATION_DIALOG.SUBMIT_LABEL;
  }

  private validateRejectCommentsAndReview(action: string): void {
    const month = this.getSelectedMonth();
    const commentRequests = this.selectedRows.map((row) =>
      this.timesheetCalendarService
        .getLeaveComments(row.employeeId, month, this.selectedyear)
        .pipe(
          map((response: LeaveCommentsResponse) =>
            (response?.data || []).some((comment) => !!comment.managerComments?.trim()),
          ),
          catchError(() => of(false)),
        ),
    );

    forkJoin(commentRequests).subscribe((commentStatusList) => {
      const hasMandatoryComments = commentStatusList.every(Boolean);

      if (!hasMandatoryComments) {
        this.toasterService.showError(
          'Comments are mandatory for rejecting timesheet',
          TIMESHEET_CONSTANTS.TITLES.ERROR,
        );
        return;
      }

      this.updateTimesheetStatus(action);
    });
  }

  private updateTimesheetStatus(action: string): void {
    const employeeIds = this.selectedRows?.map((item) => item.employeeId);
    const userProfile = this.dataService?.userProfile();
    const loggedInUserName = userProfile?.firstName;
    const payload = {
      employeeId: employeeIds,
      reviewedBy: loggedInUserName,
      status: action,
      month: this.getSelectedMonth(),
      year: this.selectedyear,
    };

    this.timesheetService.updateTimesheetStatus(payload).subscribe({
      next: (response: TimesheetUpdateResponse) => {
        this.updateLocalTimesheetStatus(employeeIds, action);
        this.selectedRows?.forEach((row) => (row.selected = false));
        this.selectedRows = [];
        this.toasterService.showSuccess(response?.reason, response?.status);

        this.isFilteredData = false;
        this.filteredData = [];
        this.activeFilterParams = {};
        this.filterValue = '';
        this.filterResetTrigger++;

        if (this.employeeId) {
          this.loadTimesheetData(
            this.activeEmployeeId || this.employeeId,
            this.getSelectedMonth(),
            this.selectedyear,
          );
        }
      },
      error: (error) => {
        this.toasterService.showError(
          TIMESHEET_CONSTANTS.MESSAGES.FAILED_UPDATE_TIMESHEET_STATUS,
          TIMESHEET_CONSTANTS.TITLES.ERROR
        );
      },
    });
  }

  private updateLocalTimesheetStatus(employeeIds: string[], action: string): void {
    const updateRows = (rows: TimeSheetData[]) =>
      rows.map((row) =>
        employeeIds.includes(row.employeeId)
          ? {
              ...row,
              status: action,
              selected: false,
            }
          : row,
      );

    this.rowData = updateRows(this.rowData);
    this.filteredData = updateRows(this.filteredData);
    this.displayData = sortByStatusPriority(
      updateRows(this.displayData),
      'vendor-management',
    );
  }

  onRowSelected(selectedRows: TimeSheetData[]) {
    localStorage.setItem(
      'selectedBeforeFilter',
      JSON.stringify(selectedRows.map((row) => row.employeeId)),
    );
    const filteredRows = filterActionableRows(selectedRows || []);
    this.selectedRows = [...filteredRows];

    localStorage.setItem(
      'selectedAfterFilter',
      JSON.stringify(this.selectedRows.map((row) => row.employeeId)),
    );
  }

  isSubmitDisabled(): boolean {
    return this.selectedRows.length === 0;
  }

  isApproveDisabled(): boolean {
    if (this.selectedRows.length === 0) return true;
    return this.selectedRows.some(
      (row) =>
        row.pendingSubmission ||
        row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION ||
        (row.status === null && row.workingHrs === 0 && row.billableHrs === 0),
    );
  }

  openTimesheetCalendar(rowData: TimeSheetData): void {
    this.selectedRowData = {
      employeeId: rowData.employeeId,
      employeeName: rowData.employeeName,
      jobTitle: rowData.jobTitle,
      location: rowData.location,
      vendorId: rowData.vendorId,
      contractorName: rowData.contractorName,
      lastWorkingDay: rowData.lastWorkingDay,
      lastHireDate: rowData.lastHireDate,
      billingDates: (rowData as any).billingDates,
    };
    this.isTimesheetCalendarOpen = true;
  }

  closeTimesheetCalendar(): void {
    this.isTimesheetCalendarOpen = false;
    this.selectedRowData = {
      employeeId: '',
      employeeName: '',
      contractorName: '',
      jobTitle: '',
      location: null,
      vendorId: null
    };
  }

  shouldShowCheckbox(row: TimeSheetData): boolean {
    return shouldShowTimesheetCheckbox(row, 'vendor-management' as ComponentType);
  }

  onCheckboxChange(row: TimeSheetData, event: any): void {
    const validation = validateTimesheetRow(row);

    if (!validation.isActionable) {
      return;
    }

    row.selected = event.target?.checked || false;

    if (row.selected) {
      if (
        !this.selectedRows.find(
          (selectedRow) => selectedRow.employeeId === row.employeeId,
        )
      ) {
        this.selectedRows.push(row);
      }
    } else {
      this.selectedRows = this.selectedRows.filter(
        (selectedRow) => selectedRow.employeeId !== row.employeeId,
      );
    }
  }

  private getSelectedMonth(): number {
    return this.selectedmonth;
  }

  private updateDataLoadedStatus(data: TimeSheetData[]): void {
    const status = getDataLoadedStatus(data, 'vendor-management' as ComponentType);
    this.isDataLoaded = status.isDataLoaded;
    this.errorMsg = status.errorMsg;
  }

  getHoursSuffix(): string {
    return getHoursSuffix();
  }

  download(): void {
    const columnsWithoutCalendar = exportColumnDefs.filter(
      (col) => col.field !== 'action',
    );
    const exportRows = this.prepareExportRows(this.selectedRows);
    this.exportService.handleExport(exportRows, columnsWithoutCalendar, 'timesheet');
  }

  private prepareExportRows(rows: any[]): any[] {
    return rows.map(row => {
      const isApproved = row.status === TimesheetStatus.Approved;
      const isPendingApproval = row.status === TimesheetStatus.PendingApproval;
      return {
        ...row,
        reviewedBy: isApproved ? row.reviewedBy : '',
        reviewedDate: isApproved ? row.reviewedDate : '',
        submittedBy: isPendingApproval ? row.reviewedBy : '',
        submittedDate: isPendingApproval ? row.reviewedDate : '',
      };
    });
  }

  private loadDirectReportees(employeeId: string): void {
    this.teamReporteesService.getReporteesList(employeeId).subscribe({
      next: (response) => {
        this.reporteeData = this.teamReporteesService.transformReporteesToDropdownOptions(response);
      },
      error: () => {
        this.reporteeData = [{ label: 'All Teams', value: 'all', selected: true }];
      }
    });
  }

  onReporteesChange(event: any): void {
    const selectedValue = event?.value || event;
    this.selectedEmployee = selectedValue;

    const employeeIdToLoad =
      selectedValue === 'all' || selectedValue === 'dr'
        ? this.employeeId!
        : selectedValue;

    this.activeEmployeeId = employeeIdToLoad;
    this.activeFilterType = selectedValue === 'dr' ? 'DirectReportee' : undefined;
    this.filteredData = [];
    this.isFilteredData = false;
    this.activeFilterParams = {};
    this.filterValue = '';
    this.selectedRows = [];

    this.loadTimesheetData(employeeIdToLoad, this.getSelectedMonth(), this.selectedyear);
  }
}